import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiClients, API_V_LATEST } from "@/axios-config";
import {
    SystemBannerCreateRequestDto,
    SystemBannerUpdateRequestDto,
} from "@/api";
import { handleErrorToast } from "@/lib/utils";
import { toast } from "sonner";
import i18next from "i18next";
import { Roles } from "@/auth/user-roles";
import {
    useRoleGatedQuery,
    useRoleGatedMutation,
} from "@/hooks/use-role-gated-hooks";
import { invalidateQueryKeys } from "@/hooks/query-invalidation";
import {
    STALE_TIME_1_MINUTE,
    REFETCH_INTERVAL_5_MINUTES,
} from "@/hooks/query-config";

export const SystemBannerQueryKeys = {
    active: "system-banner-active",
    all: "system-banners-all",
};

export const useActiveBanner = () => {
    return useQuery({
        queryKey: [SystemBannerQueryKeys.active],
        queryFn: async () => {
            try {
                const res =
                    await ApiClients.SystemBanner.apiSystembannerActiveGet(
                        API_V_LATEST,
                    );
                if (res.status === 204) return null;
                return res.data;
            } catch (err) {
                void handleErrorToast(err);
                return null;
            }
        },
        staleTime: STALE_TIME_1_MINUTE,
        refetchInterval: REFETCH_INTERVAL_5_MINUTES,
    });
};

export const useAllBanners = () => {
    return useRoleGatedQuery([Roles.ADMIN], {
        queryKey: [SystemBannerQueryKeys.all],
        queryFn: async () => {
            try {
                const res =
                    await ApiClients.SystemBanner.apiSystembannerAllGet(
                        API_V_LATEST,
                    );
                return res.data;
            } catch (err) {
                void handleErrorToast(err);
                return [];
            }
        },
    });
};

export const useCreateBanner = () => {
    const queryClient = useQueryClient();

    return useRoleGatedMutation([Roles.ADMIN], {
        mutationFn: async (data: SystemBannerCreateRequestDto) => {
            const res = await ApiClients.SystemBanner.apiSystembannerCreatePost(
                API_V_LATEST,
                data,
            );
            return res.data;
        },
        onSuccess: () => {
            invalidateQueryKeys(queryClient, [
                SystemBannerQueryKeys.all,
                SystemBannerQueryKeys.active,
            ]);
            toast.success(i18next.t("pages.admin.system-banner.created"));
        },
        onError: handleErrorToast,
    });
};

export const useUpdateBanner = () => {
    const queryClient = useQueryClient();

    return useRoleGatedMutation([Roles.ADMIN], {
        mutationFn: async (data: SystemBannerUpdateRequestDto) => {
            const res = await ApiClients.SystemBanner.apiSystembannerUpdatePut(
                API_V_LATEST,
                data,
            );
            return res.data;
        },
        onSuccess: () => {
            invalidateQueryKeys(queryClient, [
                SystemBannerQueryKeys.all,
                SystemBannerQueryKeys.active,
            ]);
            toast.success(i18next.t("pages.admin.system-banner.updated"));
        },
        onError: handleErrorToast,
    });
};

export const useDeleteBanner = () => {
    const queryClient = useQueryClient();

    return useRoleGatedMutation([Roles.ADMIN], {
        mutationFn: async (id: number) => {
            const res = await ApiClients.SystemBanner.apiSystembannerIdDelete(
                id,
                API_V_LATEST,
            );
            return res.data;
        },
        onSuccess: () => {
            invalidateQueryKeys(queryClient, [
                SystemBannerQueryKeys.all,
                SystemBannerQueryKeys.active,
            ]);
            toast.success(i18next.t("pages.admin.system-banner.deleted"));
        },
        onError: handleErrorToast,
    });
};
